import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiCalendar,
  FiUpload,
  FiRefreshCw,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiAlertCircle,
  FiEye,
  FiClock,
  FiBookOpen,
  FiTarget,
  FiTool,
  FiUser
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../api';
import LessonPlanModal from './LessonPlanModal';
import BulkUploadModal from './BulkUploadModal';

const LessonPlanManager = ({ classItem, subject, labBatch, facultyId, refreshKey, onRefresh }) => {
  const [lessonPlans, setLessonPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);
  const itemsPerPage = 10;

  const fetchLessonPlans = useCallback(async () => {
    if (!classItem?.classId || !subject?.subjectId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (labBatch?.batchId) params.append('labBatchId', labBatch.batchId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const url = `/api/faculty/lms/lesson-plans/${classItem.classId}/${subject.subjectId}?${params}`;
      const response = await api.get(url);
      
      if (response.data?.success) {
        setLessonPlans(response.data.data.lessonPlans || []);
        setSummary(response.data.data.summary || null);
      }
    } catch (error) {
      console.error('Error fetching lesson plans:', error);
    } finally {
      setLoading(false);
    }
  }, [classItem, subject, labBatch, statusFilter]);

  useEffect(() => {
    fetchLessonPlans();
  }, [fetchLessonPlans, refreshKey]);

  const handleDelete = async (planId) => {
    try {
      await api.delete(`/api/faculty/lms/lesson-plan/${planId}`);
      setShowDeleteConfirm(null);
      fetchLessonPlans();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
    }
  };

  const handleComplete = async (planId, data) => {
    try {
      await api.put(`/api/faculty/lms/lesson-plan/${planId}/complete`, data);
      fetchLessonPlans();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error completing lesson plan:', error);
    }
  };

  const handleViewDetails = (plan) => {
    setViewingPlan(plan);
    setShowViewModal(true);
  };

  const getStatusBadge = (status) => {
    const configs = {
      planned: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Planned' },
      completed: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Completed' },
      rescheduled: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Rescheduled' },
      dropped: { color: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Dropped' },
      partial: { color: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Partially Done' }
    };
    return configs[status] || configs.planned;
  };

  const getStatusIcon = (status) => {
    const configs = {
      planned: FiClock,
      completed: FiCheckCircle,
      rescheduled: FiClock,
      dropped: FiX,
      partial: FiClock
    };
    return configs[status] || FiClock;
  };

  const filteredPlans = lessonPlans.filter(plan => {
    const searchMatch = plan.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       plan.subtopics?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    return searchMatch;
  });

  const paginatedPlans = filteredPlans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);

  // Responsive Drawer/Modal Variants
  const drawerVariants = {
    hidden: { 
      y: window.innerWidth < 640 ? '100%' : 15, 
      scale: window.innerWidth < 640 ? 1 : 0.95,
      opacity: window.innerWidth < 640 ? 1 : 0 
    },
    visible: { 
      y: 0, 
      scale: 1, 
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 350 }
    },
    exit: { 
      y: window.innerWidth < 640 ? '100%' : 10, 
      scale: window.innerWidth < 640 ? 1 : 0.95,
      opacity: window.innerWidth < 640 ? 1 : 0,
      transition: { duration: 0.15 }
    }
  };

  // View Details Modal Component
  const ViewDetailsModal = ({ isOpen, onClose, plan }) => {
    if (!plan) return null;

    const StatusIcon = getStatusIcon(plan.status);
    const statusConfig = getStatusBadge(plan.status);

    return (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-100 max-w-2xl w-full max-h-[88vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
            >
              {/* Mobile Notch handle */}
              <div className="w-full flex justify-center py-2 sm:hidden bg-slate-50 border-b border-slate-100/60">
                <div className="w-12 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                    <FiBookOpen className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Lesson Plan Details</h3>
                    <p className="text-xs text-slate-500 font-medium">Lesson Plan #{plan.lessonNumber}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1">
                {/* Status Badges Row */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${statusConfig.color}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusConfig.label}
                  </span>
                  {plan.isDelayed && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 border border-rose-200 text-rose-700">
                      <FiAlertCircle className="w-3.5 h-3.5" />
                      Delayed
                    </span>
                  )}
                  {plan.isExtraClass && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 border border-purple-200 text-purple-700">
                      <FiPlus className="w-3.5 h-3.5" />
                      Extra Class
                    </span>
                  )}
                </div>

                {/* Main Info Block */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-bold tracking-wider text-indigo-600 uppercase">Main Topic</span>
                  <h4 className="text-base font-semibold text-slate-900 leading-snug">{plan.topic}</h4>
                </div>

                {/* Split Details Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Subtopics */}
                    {plan.subtopics && plan.subtopics.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-xs font-bold tracking-wide text-slate-400 uppercase flex items-center gap-1.5">
                          Subtopics Covered
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {plan.subtopics.map((sub, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg shadow-sm">
                              {sub}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Learning Objectives */}
                    {plan.learningObjectives && plan.learningObjectives.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-xs font-bold tracking-wide text-slate-400 uppercase">
                          Goals / Aim
                        </label>
                        <ul className="space-y-2">
                          {plan.learningObjectives.map((obj, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-600 leading-normal">
                              <FiTarget className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                              <span>{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Dates Card */}
                    <div className="p-3.5 bg-slate-50/60 rounded-xl border border-slate-100 space-y-2.5">
                      <label className="text-xs font-bold tracking-wide text-slate-400 uppercase block">Timetable Dates</label>
                      <div className="flex items-center gap-2.5 text-xs text-slate-600">
                        <FiCalendar className="w-4 h-4 text-slate-400" />
                        <span><strong>Planned:</strong> {new Date(plan.plannedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      {plan.actualDate && (
                        <div className="flex items-center gap-2.5 text-xs text-emerald-700">
                          <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                          <span><strong>Held On:</strong> {new Date(plan.actualDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>

                    {/* Method & Materials */}
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-bold tracking-wide text-slate-400 uppercase block mb-1.5">Teaching Method</label>
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-md">
                          <FiTool className="w-3.5 h-3.5 text-slate-400" />
                          <span className="capitalize">{plan.teachingMethodology || 'Normal Lecture'}</span>
                        </div>
                      </div>

                      {plan.resourcesUsed && plan.resourcesUsed.length > 0 && (
                        <div>
                          <label className="text-xs font-bold tracking-wide text-slate-400 uppercase block mb-1.5">Materials / Books Used</label>
                          <div className="flex flex-wrap gap-1">
                            {plan.resourcesUsed.map((resource, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded border border-indigo-100">
                                {resource}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                {plan.additionalNotes && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold tracking-wide text-slate-400 uppercase block">Remarks / Extra Notes</label>
                    <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed font-normal">{plan.additionalNotes}</p>
                  </div>
                )}

                {/* History Log */}
                {plan.versionHistory && plan.versionHistory.length > 1 && (
                  <div className="pt-4 border-t border-slate-100 space-y-2">
                    <label className="text-xs font-bold tracking-wide text-slate-400 uppercase block">Edit Log / History</label>
                    <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                      {plan.versionHistory.slice().reverse().map((version, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                          <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-bold mt-0.5">v{version.version}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-700 font-medium truncate">{version.topic}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              {version.updatedAt ? new Date(version.updatedAt).toLocaleString() : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 sm:px-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <FiUser className="w-3.5 h-3.5" />
                  <span>Added by: <strong>{plan.facultyName || 'Staff'}</strong></span>
                </div>
                <div className="flex gap-2 w-full sm:w-auto justify-end">
                  <button
                    onClick={onClose}
                    className="flex-1 sm:flex-none px-4 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 font-medium text-xs rounded-lg transition-colors shadow-sm"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      setEditingPlan(plan);
                      setShowModal(true);
                    }}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm shadow-indigo-100"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                    Modify Details
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Metrics Dashboard Row */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Lesson Plans</p>
              <h4 className="text-2xl font-bold text-slate-800 mt-1">{summary.total}</h4>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500">
              <FiBookOpen className="w-5 h-5" />
            </div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Completed Classes</p>
              <h4 className="text-2xl font-bold text-emerald-600 mt-1">{summary.completed}</h4>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-500">
              <FiCheckCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Completion Rate</p>
              <h4 className="text-2xl font-bold text-indigo-600 mt-1">{summary.completionRate}%</h4>
            </div>
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-500">
              <FiTarget className="w-5 h-5" />
            </div>
          </div>
        </div>
      )}

      {/* Primary Toolbar Actions */}
      <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-800">Lesson Plans Tracker</h2>
            <p className="text-xs text-slate-400 font-medium">Manage and update lesson plan tracking records here</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-stretch sm:justify-end">
            <button
              onClick={fetchLessonPlans}
              className="p-2 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-all"
              title="Refresh Data"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowBulkUpload(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 font-semibold rounded-lg text-xs transition-all shadow-sm"
            >
              <FiUpload className="w-3.5 h-3.5" />
              Excel Upload
            </button>
            <button
              onClick={() => {
                setEditingPlan(null);
                setShowModal(true);
              }}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs transition-all shadow-sm shadow-indigo-100"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Create Plan
            </button>
          </div>
        </div>

        {/* Filter and Search Inner Bar */}
        <div className="flex flex-col md:flex-row gap-3 pt-2 border-t border-slate-100">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by lesson plan topic or subtopics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs text-slate-700 transition-all placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center bg-slate-100/80 p-1 rounded-lg border border-slate-200/40 overflow-x-auto no-scrollbar">
            {['all', 'planned', 'completed', 'rescheduled', 'partial'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all uppercase tracking-wide text-[10px] whitespace-nowrap ${
                  statusFilter === status
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {status === 'all' ? 'Show All' : status === 'partial' ? 'Partial' : status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200/80 shadow-sm space-y-3">
          <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-600 border-t-transparent"></div>
          <p className="text-xs text-slate-400 font-medium">Getting latest details...</p>
        </div>
      ) : paginatedPlans.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200/80 shadow-sm px-4">
          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-100 text-slate-400">
            <FiCalendar className="w-5 h-5" />
          </div>
          <p className="text-sm font-semibold text-slate-700">No records found</p>
          <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">Try modifying your search or click "Create Plan" to set up a new lesson plan.</p>
        </div>
      ) : (
        <div className="bg-white sm:rounded-xl border border-slate-200/80 shadow-sm overflow-hidden rounded-xl">
          {/* Table View (Hidden on extra small devices, custom mobile list shows instead) */}
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-16 text-center">No.</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">Lesson Plan Topic Details</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-36">Schedule</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-28 text-center">Status</th>
                  <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-40 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedPlans.map((plan) => {
                  const statusConfig = getStatusBadge(plan.status);
                  const isDelayed = plan.isDelayed;
                  
                  return (
                    <tr 
                      key={plan._id}
                      className="hover:bg-slate-50/60 transition-colors cursor-pointer group"
                      onClick={() => handleViewDetails(plan)}
                    >
                      <td className="p-4 text-center text-xs font-bold text-slate-400 bg-slate-50/30">
                        #{plan.lessonNumber}
                      </td>
                      <td className="p-4 max-w-md">
                        <div className="space-y-1">
                          <h3 className="text-xs font-bold text-slate-700 group-hover:text-indigo-600 transition-colors line-clamp-1">
                            {plan.topic}
                          </h3>
                          {plan.subtopics && plan.subtopics.length > 0 && (
                            <div className="flex flex-wrap gap-1 max-h-5 overflow-hidden">
                              {plan.subtopics.map((sub, idx) => (
                                <span key={idx} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.2 rounded border border-slate-200/40 font-medium">
                                  {sub}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4 space-y-1 text-slate-600">
                        <div className="flex items-center gap-1 text-[11px]">
                          <FiCalendar className="w-3 h-3 text-slate-400" />
                          <span>P: {new Date(plan.plannedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                        </div>
                        {plan.actualDate && (
                          <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                            <FiCheckCircle className="w-3 h-3 text-emerald-500" />
                            <span>A: {new Date(plan.actualDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold border tracking-wide uppercase ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                          {isDelayed && (
                            <span className="inline-block px-1.5 py-0.2 rounded bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-bold tracking-wide uppercase">
                              Delayed
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleViewDetails(plan)}
                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-lg transition-all"
                            title="Open View"
                          >
                            <FiEye className="w-3.5 h-3.5" />
                          </button>
                          {plan.status === 'planned' && (
                            <button
                              onClick={() => handleComplete(plan._id, { actualDate: new Date().toISOString(), completionPercentage: 100 })}
                              className="w-7 h-7 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 border border-transparent hover:border-emerald-100 rounded-lg transition-all"
                              title="Mark as Done"
                            >
                              <FiCheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditingPlan(plan);
                              setShowModal(true);
                            }}
                            className="w-7 h-7 flex items-center justify-center text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-lg transition-all"
                            title="Edit Info"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(plan._id)}
                            className="w-7 h-7 flex items-center justify-center text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all"
                            title="Remove"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Responsive Mobile Layout (List View) */}
          <div className="block sm:hidden divide-y divide-slate-100 bg-white">
            {paginatedPlans.map((plan) => {
              const statusConfig = getStatusBadge(plan.status);
              return (
                <div 
                  key={plan._id} 
                  className="p-4 space-y-3 active:bg-slate-50 transition-colors"
                  onClick={() => handleViewDetails(plan)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold text-slate-400">#{plan.lessonNumber}</span>
                        <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold border uppercase tracking-wide ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        {plan.isDelayed && (
                          <span className="px-1.5 py-0.2 rounded bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-bold uppercase tracking-wide">
                            Delayed
                          </span>
                        )}
                      </div>
                      <h3 className="text-xs font-bold text-slate-800 line-clamp-2 leading-snug">{plan.topic}</h3>
                    </div>
                  </div>

                  {plan.subtopics && plan.subtopics.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {plan.subtopics.slice(0, 3).map((sub, idx) => (
                        <span key={idx} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.2 rounded border border-slate-200/40 font-medium">
                          {sub}
                        </span>
                      ))}
                      {plan.subtopics.length > 3 && (
                        <span className="text-[9px] text-slate-400 self-center font-medium">+{plan.subtopics.length - 3} more</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-1 border-t border-slate-100/60">
                    <div className="flex flex-col gap-0.5 text-[10px] text-slate-500 font-medium">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-3 h-3 text-slate-400" /> Planned: {new Date(plan.plannedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                      {plan.actualDate && (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <FiCheckCircle className="w-3 h-3 text-emerald-500" /> Held On: {new Date(plan.actualDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {plan.status === 'planned' && (
                        <button
                          onClick={() => handleComplete(plan._id, { actualDate: new Date().toISOString(), completionPercentage: 100 })}
                          className="w-7 h-7 flex items-center justify-center text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg transition-all"
                          title="Mark as Done"
                        >
                          <FiCheckCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingPlan(plan);
                          setShowModal(true);
                        }}
                        className="w-7 h-7 flex items-center justify-center text-blue-600 bg-blue-50 border border-blue-100 rounded-lg transition-all"
                        title="Edit Info"
                      >
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(plan._id)}
                        className="w-7 h-7 flex items-center justify-center text-rose-600 bg-rose-50 border border-rose-100 rounded-lg transition-all"
                        title="Remove"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Simple Clean Pagination Footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors shadow-sm"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-semibold text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors shadow-sm"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Drawer / Modal Box */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-t-2xl sm:rounded-2xl max-w-sm w-full p-5 border border-slate-100 shadow-xl"
            >
              <div className="w-full flex justify-center pb-2 sm:hidden">
                <div className="w-12 h-1 bg-slate-200 rounded-full" />
              </div>
              <div className="flex items-center gap-3 text-rose-600 mb-3 mt-2 sm:mt-0">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500 border border-rose-100">
                  <FiAlertCircle className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">Confirm Deletion</h3>
              </div>
              <p className="text-xs text-slate-500 leading-normal mb-5">
                Are you sure you want to remove this lesson plan record? This process cannot be undone.
              </p>
              <div className="flex gap-2.5 justify-stretch sm:justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 sm:flex-none px-3.5 py-2 text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold rounded-lg shadow-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 sm:flex-none px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg shadow-sm shadow-rose-100 transition-colors"
                >
                  Delete Plan
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* External Form & Feature Modals */}
      <ViewDetailsModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingPlan(null);
        }}
        plan={viewingPlan}
      />

      <LessonPlanModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPlan(null);
        }}
        onSuccess={() => {
          fetchLessonPlans();
          if (onRefresh) onRefresh();
        }}
        classItem={classItem}
        subject={subject}
        labBatch={labBatch}
        facultyId={facultyId}
        editingPlan={editingPlan}
      />

      <BulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={() => {
          fetchLessonPlans();
          if (onRefresh) onRefresh();
        }}
        classItem={classItem}
        subject={subject}
        labBatch={labBatch}
        facultyId={facultyId}
        type="lesson-plans"
        templateEndpoint="/api/faculty/lms/lesson-plan/template"
        uploadEndpoint="/api/faculty/lms/lesson-plans/bulk-upload-excel"
      />
    </div>
  );
};

export default LessonPlanManager;